<template>
  <div class="main-layout">
    <!-- Mobile header with hamburger -->
    <header class="main-layout__header">
      <button
        class="main-layout__menu-btn"
        :aria-label="$t('action.open_menu')"
        @click="sidebarOpen = !sidebarOpen"
      >
        <Bars3Icon class="main-layout__menu-icon" />
      </button>
      <span class="main-layout__header-title">{{ $t('app.name') }}</span>
    </header>

    <!-- Sidebar -->
    <AppSidebar
      :is-open="sidebarOpen"
      :current-user="authStore.currentUser"
      @navigate="sidebarOpen = false"
      @logout="handleLogout"
    />

    <!-- Mobile overlay -->
    <div v-if="sidebarOpen" class="main-layout__overlay" @click="sidebarOpen = false" />

    <!-- Main content -->
    <main id="main-content" class="main-layout__content">
      <!-- No <keep-alive>: the report chart stores are singletons shared by
           both `/` (PerformanceCharts source="me") and `/dashboards`
           (source="scoped"). Keeping views alive would let both mount at once
           and race on that shared state. Keep this a plain, unwrapped
           router-view. -->
      <router-view v-slot="{ Component }">
        <component :is="Component" />
      </router-view>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { Bars3Icon } from '@heroicons/vue/24/outline'
import AppSidebar from '@/components/organisms/AppSidebar.vue'
import { useAuthStore } from '@/stores/auth.store'

const sidebarOpen = ref(false)
const authStore = useAuthStore()
const router = useRouter()

function handleLogout() {
  authStore.logout()
  router.push({ name: 'login' })
}
</script>

<style scoped lang="scss">
.main-layout {
  min-height: 100vh;
  background-color: var(--cds-background, #f4f4f4);
}

// Mobile header - hidden on desktop
.main-layout__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background-color: var(--app-sidebar-bg);
  color: var(--app-sidebar-text);
  position: sticky;
  top: 0;
  z-index: 8000;

  @media (min-width: 1056px) {
    display: none;
  }
}

.main-layout__menu-btn {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-layout__menu-icon {
  width: 24px;
  height: 24px;
}

.main-layout__header-title {
  font-size: var(--text-size-lg);
  font-weight: var(--font-weight-semibold);
}

// Mobile overlay backdrop
.main-layout__overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 8500;

  @media (min-width: 1056px) {
    display: none;
  }
}

// Main content area
.main-layout__content {
  min-height: 100vh;
  padding: 24px;

  @media (min-width: 1056px) {
    margin-left: var(--app-sidebar-width);
    padding: 32px 40px;
  }
}
</style>
